import {DataStatus, IconButton, TextInput} from 'platform/components';
import {Box, HStack, Show, Text} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {useEffect, useState} from 'react';

import {isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {
  useGetServiceVehicleAdditionalInformationQuery,
  useGetServiceVehicleV2Query,
  usePatchServiceVehicleAdditionalInformationMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {EMPTY_PLACEHOLDER, parseDate} from 'shared/web';

interface VehicleAdditionalInformationProps {
  vehicleId: string;
}

const getTranslationKey = (code: string | null, type: 'label' | 'description'): string => {
  if (isNil(code)) {
    return '';
  }

  const normalizedCode = code.charAt(0).toLowerCase() + code.slice(1);

  return `entity.vehicle.additionalInformation.${type}s.${normalizedCode}`;
};

const getFieldTranslation = (code: string | null, type: 'label' | 'description'): string => {
  const translationKey = getTranslationKey(code, type);
  const translated = i18n.t(translationKey);

  if (translated !== translationKey) {
    return translated;
  }

  return translationKey;
};

export function VehicleAdditionalInformation(props: VehicleAdditionalInformationProps) {
  const formatDateTime = useDateTimeFormatter();

  const {
    data: serviceVehicle,
    isLoading: isServiceVehicleLoading,
    isError: isServiceVehicleError,
  } = useGetServiceVehicleV2Query({vehicleId: props.vehicleId});

  const {
    data: additionalInformation,
    isLoading: isAdditionalInformationLoading,
    isError: isAdditionalInformationError,
  } = useGetServiceVehicleAdditionalInformationQuery(
    {serviceVehicleId: serviceVehicle?.serviceVehicleId ?? ''},
    {skip: !serviceVehicle?.serviceVehicleId}
  );

  const [patchAdditionalInformation, {isLoading: isPatching}] =
    usePatchServiceVehicleAdditionalInformationMutation();

  // Track which items have pending saves to prevent clearing editing state too early
  const [pendingSaves, setPendingSaves] = useState<Record<string, string>>({});

  const [editingStates, setEditingStates] = useState<Record<string, string>>({});

  const isLoading = isServiceVehicleLoading || isAdditionalInformationLoading;
  const isError = isServiceVehicleError || isAdditionalInformationError;

  const getStringValue = (value: Record<string, unknown> | null): string => {
    if (isNil(value)) {
      return '';
    }

    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'object' && 'value' in value) {
      return String(value.value);
    }

    return String(value);
  };

  const formatValue = (value: Record<string, unknown> | null, valueType: string): string | null => {
    if (isNil(value)) {
      return null;
    }

    return match(valueType)
      .with('date', () =>
        isNil(value) ? null : formatDateTime('dateShort', parseDate(value.toString()))
      )
      .with('dateTime', () =>
        isNil(value) ? null : formatDateTime('dateTimeShort', parseDate(value.toString()))
      )
      .with('boolean', () => (value ? i18n.t('general.labels.yes') : i18n.t('general.labels.no')))
      .otherwise(() => (value ? value.toString() : EMPTY_PLACEHOLDER));
  };

  // Use API response directly - display all fields sent by the backend
  const items = additionalInformation ?? [];

  // Clear editing state when the saved value matches what we sent
  // This prevents the input from appearing empty during refetch
  useEffect(() => {
    setEditingStates((prev) => {
      const newState = {...prev};
      let hasChanges = false;

      for (const [code, pendingValue] of Object.entries(pendingSaves)) {
        const item = items.find((i) => i.code === code);
        if (isNotNil(item)) {
          const currentValue = getStringValue(item.value);
          // If the server value matches what we saved, clear both editing and pending
          if (currentValue === pendingValue) {
            delete newState[code];
            hasChanges = true;
          }
        }
      }

      if (hasChanges) {
        // Also clear from pending saves
        setPendingSaves((prevPending) => {
          const newPending = {...prevPending};
          for (const code of Object.keys(pendingSaves)) {
            const item = items.find((i) => i.code === code);
            if (isNotNil(item) && getStringValue(item.value) === pendingSaves[code]) {
              delete newPending[code];
            }
          }
          return newPending;
        });
      }

      return hasChanges ? newState : prev;
    });
  }, [items, pendingSaves]);

  const handleCancel = (itemCode: string | null) => {
    if (isNil(itemCode)) {
      return;
    }

    setEditingStates((prev) => {
      const newState = {...prev};
      delete newState[itemCode];
      return newState;
    });

    // Also clear from pending saves if it was there
    setPendingSaves((prev) => {
      if (!(itemCode in prev)) {
        return prev;
      }
      const newState = {...prev};
      delete newState[itemCode];
      return newState;
    });
  };

  const handleSave = (itemCode: string) => {
    const item = items.find((item) => item.code === itemCode);
    if (isNil(item) || isNil(serviceVehicle?.serviceVehicleId) || isNil(item.id)) {
      return;
    }

    const editedValue = editingStates[itemCode] ?? getStringValue(item.value);

    // Store the value we're saving so we can clear editing state when server confirms
    setPendingSaves((prev) => ({...prev, [itemCode]: editedValue}));

    patchAdditionalInformation({
      serviceVehicleId: serviceVehicle.serviceVehicleId,
      additionalInformationId: item.id,
      patchAdditionalInformationRequestBody: {value: editedValue},
    })
      .unwrap()
      .catch(handleApiError);
  };

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <Box borderRadius="small" boxShadow="elevation_1" backgroundColor="palettes.white.10.100">
        <Box
          paddingHorizontal={4}
          paddingVertical={2}
          backgroundColor="palettes.neutral.10.100"
          borderColor="palettes.neutral.40.100"
          borderBottom="1px solid"
        >
          <HStack width="100%">
            <Box flex={1} borderColor="palettes.neutral.40.100">
              <Text color="secondary">
                {i18n.t('entity.vehicle.additionalInformation.tableHeaders.name')}
              </Text>
            </Box>
            <Box
              flex={1}
              borderLeft="1px solid"
              borderRight="1px solid"
              borderColor="palettes.neutral.40.100"
              paddingHorizontal={4}
            >
              <Text color="secondary">
                {i18n.t('entity.vehicle.additionalInformation.tableHeaders.description')}
              </Text>
            </Box>
            <Box flex={1} borderColor="palettes.neutral.40.100" paddingHorizontal={4}>
              <Text color="secondary">
                {i18n.t('entity.vehicle.additionalInformation.tableHeaders.value')}
              </Text>
            </Box>
          </HStack>
        </Box>

        {items.map((item) => (
          <Box
            paddingVertical={2}
            key={item.code}
            borderBottom="1px solid"
            borderColor="general.separator"
          >
            <HStack width="100%" align="center">
              <Box paddingLeft={4} flex={1} borderColor="palettes.neutral.40.100">
                {getFieldTranslation(item.code, 'label')}
              </Box>
              <Box flex={1} borderColor="palettes.neutral.40.100">
                {getFieldTranslation(item.code, 'description')}
              </Box>
              <Box paddingHorizontal={4} flex={1} borderColor="palettes.neutral.40.100">
                <Show when={item.isEditable}>
                  <HStack spacing={2} align="center">
                    <Box flex={1}>
                      <TextInput
                        value={
                          isNotNil(item.code) && item.code in editingStates
                            ? editingStates[item.code]
                            : getStringValue(item.value)
                        }
                        onChange={(value) => {
                          const itemCode = item.code;
                          if (isNotNil(itemCode)) {
                            setEditingStates((prev) => ({
                              ...prev,
                              [itemCode]: value ?? '',
                            }));
                          }
                        }}
                        isDisabled={isPatching}
                      />
                    </Box>
                    <IconButton
                      icon="content/clear"
                      onClick={() => {
                        if (isNotNil(item.code)) {
                          handleCancel(item.code);
                        }
                      }}
                      isDisabled={isPatching}
                      aria-label={i18n.t('general.actions.cancel')}
                    />
                    <IconButton
                      icon="action/done"
                      onClick={() => {
                        if (isNotNil(item.code)) {
                          handleSave(item.code);
                        }
                      }}
                      isDisabled={isPatching}
                      aria-label={i18n.t('general.actions.save')}
                    />
                  </HStack>
                </Show>
                <Show when={!item.isEditable}>
                  <Text>{formatValue(item.value, item.valueType) ?? EMPTY_PLACEHOLDER}</Text>
                </Show>
              </Box>
            </HStack>
          </Box>
        ))}
      </Box>
    </DataStatus>
  );
}
